import axios from 'axios';
import inEU from '@segment/in-eu';
import Cookies from 'js-cookie';
import AppConstants from '../App/AppConstants';
import CreatePageConstants from '../components/pages/CreatePage/CreatePageConstants';
import { allFirstToUpper } from './stringUtilities';
import { utils as pixiUtils } from 'pixi.js';

export function safeGtag(...args) {
    if (typeof window.gtag === 'function' && window.gtag !== null) {
        window.gtag(...args);
    }
}

export function loadAnalytics() {

    if (typeof window.amplitude === 'object' && window.amplitude.getInstance) {
        window.amplitude.getInstance().init(window._analyticsKeys.amplitude);
    }

    const awScript = document.createElement('script');
    awScript.src = `https://www.googletagmanager.com/gtag/js?id=${window._analyticsKeys.google_ads}`;
    awScript.async = true;
    document.body.appendChild(awScript);
}

export function identify(userData) {
    if (typeof window.amplitude === 'object' && window.amplitude.getInstance) {
        window.amplitude.getInstance().setUserId(userData.id);

        window.amplitude.getInstance().setUserProperties({
            email: userData.email, 
            plan: userData.subscriptionType, 
            planCanceled: userData.subscriptionCanceled,
            remainingCredits: userData.subscriptionType === AppConstants.FREE_SUBSCRIPTION_TYPE ? userData.freeCredits : userData.hobbyistCredits + userData.professionalCredits,
            advancedSettings: localStorage.getItem(CreatePageConstants.SHOW_ADVANCED_SETTINGS_STORAGE_KEY) === 'true',
            videoNotificationEmails: userData.receiveVideoNotificationEmails,
            updatesAndOffersEmails: userData.receiveUpdatesAndOffersEmails,
            webGLSupported: pixiUtils.isWebGLSupported()
        });
    }
}

export function trackPage(name) {
    if (typeof window.plausible === 'function') {
        window.plausible('pageview');
    }

    const uppercaseName = allFirstToUpper(name.split('/')[1].replace('-', ' '));
    if (typeof window.amplitude === 'object' && window.amplitude.getInstance) {
        window.amplitude.getInstance().logEvent(`Viewed ${uppercaseName} Page`, {
            category: 'Web App',
            name: uppercaseName,
            path: name,
            referrer: document.referrer,
            title: document.title,
            url: document.location.href
        });
    }
}

export function trackEvent(name, properties) {
    if (typeof window.plausible === 'function') {
        window.plausible(name, { props: properties });
    }

    if (typeof window.amplitude === 'object' && window.amplitude.getInstance) {
        window.amplitude.getInstance().logEvent(name, properties);
    }
}

export function trackTransaction(properties) {
    
    if (typeof window.plausible === 'function') {
        window.plausible('ecommerce:addTransaction', { props:  {
            'id': properties.id,
            'affiliation': 'Specterr',
            'revenue': properties.price,
            'currency': properties.currency,
        }});

        window.plausible('ecommerce:addItem', { props:  {
            'id': properties.id,
            'name': properties.name,
            'price': properties.price,
            'quantity': 1,
            'currency': properties.currency,
        }});
    }

    safeGtag('event', 'conversion', {
        'send_to': 'AW-627619508/IZxJCK-V5KkDELTtoqsC',
        'value': properties.value,
        'currency': properties.currency,
    });
}

export function loadAnalyticsConsent() {
    // fetch existing consent status cookie
    var analyticsConsentStatus = Cookies.get(ANALYTICS_CONSENT_STATUS_COOKIE_KEY);

    // if there is no consent status cookie, it must be assigned
    if (typeof analyticsConsentStatus === 'undefined') {

        // fetch existing segment consent status cookie
        const segmentTrackingPreferences = Cookies.get('tracking-preferences');
        
        // if the segment cookie doesn't exist set the new consent status to untracked
        // if it does exist, then use that for the consent status
        if (typeof segmentTrackingPreferences === 'undefined') {
            analyticsConsentStatus = AnalyticsConsentStatuses.UNTRACKED;
        } else {
            const segmentTrackingPreferencesObject = JSON.parse(segmentTrackingPreferences);
            analyticsConsentStatus = (segmentTrackingPreferencesObject.destinations["Amplitude"] && segmentTrackingPreferencesObject.destinations["Google Analytics"]) ? AnalyticsConsentStatuses.OPTIN : AnalyticsConsentStatuses.OPTOUT;
        }
    }

    // apply consent status
    const isEUUser = inEU();
    setAnalyticsConsentStatus(analyticsConsentStatus, isEUUser);

    // return wether or not the consent popup should be displayed
    return (analyticsConsentStatus === AnalyticsConsentStatuses.UNTRACKED && isEUUser);
}

export function setAnalyticsConsentStatus(status, isEUUser) {
    // TODO: Replace this condition with isProduction helper
    // set cookie on root domain
    const cookieDomain = window.location.host === 'app.specterr.com' ? 'specterr.com' : null;
    Cookies.set(
        ANALYTICS_CONSENT_STATUS_COOKIE_KEY, 
        status, 
        { expires: 365, domain: cookieDomain }
    );

    // if user opted out, or they are in eu and haven't supplied consent, disable analytics transmission
    if (status === AnalyticsConsentStatuses.OPTOUT || (status === AnalyticsConsentStatuses.UNTRACKED && isEUUser)) {
        if (typeof window.amplitude === 'object' && window.amplitude.getInstance) {
            window.amplitude.getInstance().setOptOut(true);
        }
        safeGtag('consent', 'update', {
            'ad_storage': 'denied',
            'analytics_storage': 'denied'
        });
    } else {
        if (typeof window.amplitude === 'object' && window.amplitude.getInstance) {
            window.amplitude.getInstance().setOptOut(false);
        }
        safeGtag('consent', 'update', {
            'ad_storage': 'granted',
            'analytics_storage': 'granted'
        });
    }
}

axios.interceptors.response.use(function (response) {
    return response;
}, function (error) {
    if (error.isAxiosError && error.response && error.response.status !== 401) {
        trackEvent('Failed Request', {
            message: error.message,
            status: error.response.status,
            data: error.response.data,
            url: error.config.url,
            method: error.config.method,
            category: AnalyticsConstants.PERFORMANCE_CATEGORY
        })
    }

    return Promise.reject(error);
});

export const AnalyticsConstants = {
    PERFORMANCE_CATEGORY: 'Performance',
    MISC_CATEGORY: 'Misc',
    ACCOUNT_CATEGORY: 'Account',
    VIDEO_CATEGORY: 'Video',
    ACQUISITION_CATEGORY: 'Acquisition',
    ECOMMERCE_CATEGORY: 'Ecommerce',
    SOCIAL_CATEGORY: 'Social',
}

const ANALYTICS_CONSENT_STATUS_COOKIE_KEY = 'ANALYTICS_CONSENT_STATUS';

export const AnalyticsConsentStatuses = {
    UNTRACKED: 'Untracked',
    OPTIN: 'Optin',
    OPTOUT: 'Optout'
}

// A request that produced no HTTP response at all: a dropped connection, a timeout,
// or a CORS rejection.
const isNetworkFailure = (error) =>
    !error?.response && (
        error?.code === 'ERR_NETWORK' ||
        error?.code === 'ECONNABORTED' ||
        error?.message === 'Network Error' ||
        Boolean(error?.request)
    );

// The Azure upload urls carry the SAS signature in the query string.
// Strip everything after "?" so tokens never reach the analytics.
const getSafeRequestUrl = (error) => {
    const url = error?.config?.url;
    return url ? url.split('?')[0] : null;
};

const getServerErrorMessage = (error) => {
    const data = error?.response?.data;
    if (typeof data === 'string') return data.slice(0, 200);
    if (typeof data?.message === 'string') return data.message.slice(0, 200);
    return null;
};

// Hardware and network characteristics of the client. deviceMemory and connection
// are Chromium-only, so every field falls back to null instead of throwing on Safari/Firefox.
const getClientEnvironment = () => {
    if (typeof navigator === 'undefined')
        return {};

    return {
        isOffline: navigator.onLine === false,
        deviceMemoryGb: navigator.deviceMemory ?? null,
        cpuCores: navigator.hardwareConcurrency ?? null,
        connectionType: navigator.connection?.effectiveType ?? null,
        connectionDownlinkMbps: navigator.connection?.downlink ?? null,
    };
};

export function trackResourceSavingFailedEvent(resourceName, error, context = {}) {
    trackEvent('Resource Saving Failed', {
        message: `${error.message ?? 'Unknown error'} (${resourceName})`,
        stack: error.stack ?? 'Unknown stack',
        category: AnalyticsConstants.VIDEO_CATEGORY,
        resource: resourceName,
        errorName: error?.name ?? 'Unknown',
        errorCode: error?.code ?? null,
        httpStatusCode: error?.response?.status ?? null,
        serverMessage: getServerErrorMessage(error),
        isNetworkError: isNetworkFailure(error),
        requestUrl: getSafeRequestUrl(error),
        failureStage: 'Unknown',
        ...getClientEnvironment(),
        ...context,
    });
}